// The sync engine (WEB_PLAN.md §3): owns the replica of one company for one signed-in account, and schedules pulls
// and pushes the way SyncCenter does on iOS (GroundTeam/Sync/SyncCenter.swift), adapted to a browser tab (§3.4):
//
// - pull every 15 s while the tab is visible; none while hidden; pull now on visibilitychange → visible, focus,
//   online, after a push that saved anything, and when a screen asks (Refresh);
// - when the server rings (doorbell.ts: a teammate saved something), pull within RING_DEBOUNCE_MS. While the ring
//   channel is subscribed ("live") the timed pull only runs when nothing was pulled for a minute, and is back to every
//   15 s while the last pull failed or once that minute's pull finds changes no ring announced (the server stopped
//   ringing: its trigger dropped, rings lost while the socket stays up); the channel closes after the tab has been
//   hidden for a minute and reopens when it is visible again;
// - push 400 ms after the last edit, right away when the tab is hidden, on flush (sign-out) and before unload;
// - pull and push never overlap (one AsyncGate per run, like SyncEngine.acquire/release);
// - not_member / suspended stop the engine, delete this replica and tell the auth layer (SyncProvider calls
//   refreshMemberships, which routes to "removed" / "paused").
//
// The engine is restartable (React StrictMode mounts effects twice): `start` opens the IndexedDB replica for the
// session, `stop` closes it. A stopped run never writes again; operations still in flight see `isStopped` after
// their next await and drop their results.
//
// Transport and browser signals are injected (`api`, `environment`) so tests drive it with the in-memory server.

import type { FlushResult } from '@/auth/flush';
import type { Collection } from '@/contracts/collections';
import type { SessionCtx } from '@/session/types';
import { SyncApiError, describeSyncError, isSyncApiError, type SyncApi, type SyncErrorKind } from './api';
import { nowForEnvelope } from './clock';
import type { RingKind } from './doorbell';
import { AsyncGate } from './gate';
import { registerLocalData } from './local';
import { applyPatches, discardEntry, retryFailed, type SaveResult } from './outbox';
import type { Patch } from './patch';
import { pullOnce } from './pull';
import { pushOnce, type SyncNotice } from './push';
import { Replica, replicaDbName } from './replica';
import type { SyncRuntime } from './runtime';
import { INITIAL_SYNC_STATUS, type SyncStatus } from './status';

export const POLL_INTERVAL_MS = 15_000;
/** While the doorbell is live, rings bring teammates' changes: a timed pull runs only after this long without one. */
export const POLL_INTERVAL_LIVE_MS = 60_000;
export const PUSH_DEBOUNCE_MS = 400;
/** Rings this close together share one pull (several phones saving at once, a push sent in chunks). */
export const RING_DEBOUNCE_MS = 300;
/** The doorbell channel closes after the tab has been hidden this long (one Realtime connection less per tab). */
export const DOORBELL_HIDDEN_CLOSE_MS = 60_000;
/** notify-devices at most this often (iOS notifyTeammates throttle). */
export const NOTIFY_THROTTLE_MS = 20_000;
/** A focus / visibility pull is skipped when a pull finished this recently (both events fire on one tab switch). */
export const PULL_MIN_GAP_MS = 2_000;

export type RemovalKind = 'not_member' | 'suspended';

/** Thrown by `save` before the replica has loaded or after the engine stopped (the edit would be lost). */
export class SyncNotReadyError extends Error {
  constructor() {
    super('Still loading your company data. Try again in a moment.');
    this.name = 'SyncNotReadyError';
  }
}

export interface SyncEnvironmentHandlers {
  visible(): void;
  hidden(): void;
  focus(): void;
  online(): void;
  offline(): void;
  beforeUnload(event: BeforeUnloadEvent): void;
}

/** The browser signals the engine reacts to (tests pass null: no polling, no listeners). */
export interface SyncEnvironment {
  isVisible(): boolean;
  isOnline(): boolean;
  listen(handlers: SyncEnvironmentHandlers): () => void;
}

/** `document` / `window` signals; null outside a browser. */
export function browserEnvironment(): SyncEnvironment | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  return {
    isVisible: () => document.visibilityState === 'visible',
    isOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false,
    listen(h) {
      const onVisibility = () => (document.visibilityState === 'visible' ? h.visible() : h.hidden());
      const onFocus = () => h.focus();
      const onOnline = () => h.online();
      const onOffline = () => h.offline();
      const onBeforeUnload = (event: BeforeUnloadEvent) => h.beforeUnload(event);
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('focus', onFocus);
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      window.addEventListener('beforeunload', onBeforeUnload);
      return () => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
        window.removeEventListener('beforeunload', onBeforeUnload);
      };
    },
  };
}

/**
 * Starts listening for the server's rings for one company (doorbell.ts `listenForChanges` with the supabase client)
 * and returns stop. `onState(true)` while the channel is subscribed. Neither callback may run after stop.
 */
export type DoorbellFactory = (
  companyId: string,
  onRing: (kind: RingKind, seq: number | null) => void,
  onState: (live: boolean) => void,
) => () => void;

export interface SyncEngineOptions {
  api: SyncApi;
  /** Edit-time clock (default: server-corrected `nowForEnvelope`). */
  now?: () => Date;
  /** Default: a new Replica with this browser's device id. */
  replica?: Replica;
  /** Default: browserEnvironment(). */
  environment?: SyncEnvironment | null;
  pollIntervalMs?: number;
  pollIntervalLiveMs?: number;
  pushDebounceMs?: number;
  /** The server's change signal. Default: none (polling only). */
  doorbell?: DoorbellFactory | null;
}

type Timer = ReturnType<typeof setTimeout>;

interface Run {
  ctx: SessionCtx;
  runtime: SyncRuntime;
  gate: AsyncGate;
  stopped: boolean;
  /** The replica database is open and loaded. */
  ready: boolean;
  started: Promise<void>;
  pullQueued: Promise<void> | null;
  pushQueued: Promise<void> | null;
  pushTimer: Timer | null;
  pollTimer: ReturnType<typeof setInterval> | null;
  notifyTimer: Timer | null;
  lastNotifyAt: number;
  lastPullAt: number;
  detach: (() => void) | null;
  unregister: (() => void) | null;
  /** The open doorbell channel (null while closed); its callbacks check they still belong to it. */
  doorbell: { stop: () => void } | null;
  doorbellCloseTimer: Timer | null;
  ringTimer: Timer | null;
  /**
   * Rings not answered yet: the highest seq, whether one needs a pull whatever the cursor says (no seq: a reconnect
   * or a team change), and whether team screens should refetch. Held while the tab is hidden.
   */
  ring: PendingRing;
  /** `changed` rings with a seq heard so far, answered or not, and the highest seq among them. */
  ringsHeard: number;
  lastRingSeq: number;
}

interface PendingRing {
  seq: number;
  always: boolean;
  team: boolean;
}

const NO_RING: PendingRing = { seq: 0, always: false, team: false };

interface Flags {
  pulling: boolean;
  pushing: boolean;
  fullResync: boolean;
  progress: { applied: number } | null;
  error: { kind: SyncErrorKind; message: string } | null;
  offline: boolean;
  live: boolean;
  /**
   * The channel is up but the minute's safety-net pull found changes no ring announced: poll every 15 s and don't
   * show "Live" until a `changed` ring with a seq arrives again.
   */
  ringsMissed: boolean;
}

function initialFlags(): Flags {
  return {
    pulling: false,
    pushing: false,
    fullResync: false,
    progress: null,
    error: null,
    offline: false,
    live: false,
    ringsMissed: false,
  };
}

const OFFLINE_MESSAGE = "You're offline. Changes will sync when the connection is back.";

function delay(ms: number): { promise: Promise<void>; cancel: () => void } {
  let timer: Timer | undefined;
  const promise = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return { promise, cancel: () => timer !== undefined && clearTimeout(timer) };
}

function sameScope(a: SessionCtx, b: SessionCtx): boolean {
  return (
    a.authUserId === b.authUserId &&
    a.companyId.toLowerCase() === b.companyId.toLowerCase() &&
    a.role === b.role &&
    a.profileId.toLowerCase() === b.profileId.toLowerCase()
  );
}

export class SyncEngine {
  readonly replica: Replica;

  private readonly api: SyncApi;
  private readonly now: () => Date;
  private readonly environment: SyncEnvironment | null;
  private readonly pollIntervalMs: number;
  private readonly pollIntervalLiveMs: number;
  private readonly pushDebounceMs: number;
  private readonly doorbell: DoorbellFactory | null;

  private run: Run | null = null;
  private lifecycle: Promise<void> = Promise.resolve();
  private flags: Flags = initialFlags();
  private removal: RemovalKind | null = null;

  private status: SyncStatus = INITIAL_SYNC_STATUS;
  private statusListeners = new Set<() => void>();
  private noticeListeners = new Set<(notice: SyncNotice) => void>();
  private removedListeners = new Set<(kind: RemovalKind) => void>();
  private teamListeners = new Set<() => void>();

  constructor(options: SyncEngineOptions) {
    this.api = options.api;
    this.now = options.now ?? (() => nowForEnvelope());
    this.replica = options.replica ?? new Replica();
    this.environment = options.environment === undefined ? browserEnvironment() : options.environment;
    this.pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS;
    this.pollIntervalLiveMs = options.pollIntervalLiveMs ?? POLL_INTERVAL_LIVE_MS;
    this.pushDebounceMs = options.pushDebounceMs ?? PUSH_DEBOUNCE_MS;
    this.doorbell = options.doorbell ?? null;
    // Pending/failed counts follow the outbox.
    this.replica.subscribe('*', () => this.refreshStatus());
  }

  // MARK: - Lifecycle

  /** The session this engine is syncing (null when stopped). */
  get session(): SessionCtx | null {
    const run = this.run;
    return run && !run.stopped ? run.ctx : null;
  }

  /** Why the server stopped this engine, if it did. */
  get removalReason(): RemovalKind | null {
    return this.removal;
  }

  /** True once the replica is loaded and edits are accepted. */
  get isReady(): boolean {
    return this.run !== null && !this.run.stopped && this.run.ready;
  }

  /**
   * Opens the replica for `ctx` and starts syncing. Resolves when the local copy is loaded (the first pull runs in
   * the background; watch `status.initialFetchDone`). Starting again with the same scope is a no-op.
   */
  start(ctx: SessionCtx): Promise<void> {
    const current = this.run;
    if (current && !current.stopped) {
      if (sameScope(current.ctx, ctx)) return current.started;
      void this.stop();
    }
    this.removal = null;
    this.flags = initialFlags();
    const run: Run = {
      ctx,
      runtime: {
        api: this.api,
        replica: this.replica,
        companyId: ctx.companyId.toLowerCase(),
        profileId: ctx.profileId || null,
        now: this.now,
        // Read when called, after `run` is initialised.
        isStopped: () => run.stopped,
      },
      gate: new AsyncGate(),
      stopped: false,
      ready: false,
      started: Promise.resolve(),
      pullQueued: null,
      pushQueued: null,
      pushTimer: null,
      pollTimer: null,
      notifyTimer: null,
      lastNotifyAt: 0,
      lastPullAt: 0,
      detach: null,
      unregister: null,
      doorbell: null,
      doorbellCloseTimer: null,
      ringTimer: null,
      ring: NO_RING,
      ringsHeard: 0,
      lastRingSeq: 0,
    };
    this.run = run;
    run.started = this.chain(() => this.open(run));
    this.refreshStatus();
    return run.started;
  }

  /** Stops syncing and closes the database (memory is kept until the next start or a wipe). */
  stop(): Promise<void> {
    const run = this.run;
    if (!run || run.stopped) return this.lifecycle;
    this.halt(run);
    this.refreshStatus();
    return this.chain(() => this.replica.close());
  }

  /** Stops and deletes this replica (memory and IndexedDB). Used by wipeAllLocalData. */
  wipe(): Promise<void> {
    const run = this.run;
    if (run && !run.stopped) this.halt(run);
    this.refreshStatus();
    return this.chain(() => this.replica.destroy());
  }

  private chain(task: () => Promise<unknown>): Promise<void> {
    this.lifecycle = this.lifecycle
      .then(task)
      .then(
        () => undefined,
        (error) => {
          console.error('sync lifecycle step failed', error);
        },
      );
    return this.lifecycle;
  }

  private async open(run: Run): Promise<void> {
    if (run.stopped) return;
    await this.replica.open(replicaDbName(run.ctx.authUserId, run.ctx.companyId));
    if (run.stopped) return;
    run.ready = true;
    run.unregister = registerLocalData({ wipe: () => this.wipe() });
    this.attach(run);
    this.refreshStatus();
    // Pull first, then send what a previous visit left in the outbox (iOS reconcile + syncNow).
    void this.requestPull(run);
    if (this.hasPendingToPush()) void this.requestPush(run);
  }

  /** Marks a run stopped and lets go of its timers and listeners (synchronous). */
  private halt(run: Run): void {
    run.stopped = true;
    run.ready = false;
    if (run.pushTimer) clearTimeout(run.pushTimer);
    if (run.notifyTimer) clearTimeout(run.notifyTimer);
    if (run.pollTimer) clearInterval(run.pollTimer);
    if (run.ringTimer) clearTimeout(run.ringTimer);
    if (run.doorbellCloseTimer) clearTimeout(run.doorbellCloseTimer);
    run.pushTimer = run.notifyTimer = run.pollTimer = run.ringTimer = run.doorbellCloseTimer = null;
    run.ring = NO_RING;
    this.stopDoorbell(run);
    run.detach?.();
    run.detach = null;
    run.unregister?.();
    run.unregister = null;
    this.flags.pulling = this.flags.pushing = this.flags.fullResync = this.flags.live = this.flags.ringsMissed = false;
    this.flags.progress = null;
  }

  // MARK: - Browser signals and polling

  private attach(run: Run): void {
    const env = this.environment;
    if (!env) {
      this.openDoorbell(run);
      return;
    }
    this.flags.offline = !env.isOnline();
    run.detach = env.listen({
      visible: () => {
        if (run.stopped) return;
        this.cancelDoorbellClose(run);
        this.openDoorbell(run);
        this.startPolling(run);
        // Rings heard while hidden were held back: answer them now, past the minimum gap.
        if (!this.answerRing(run)) void this.requestPull(run, PULL_MIN_GAP_MS);
      },
      hidden: () => {
        if (run.stopped) return;
        this.stopPolling(run);
        this.closeDoorbellSoon(run);
        // The tab may be closing: send pending edits now instead of after the debounce.
        if (this.hasPendingToPush()) this.pushSoon(run, 0);
      },
      focus: () => {
        if (!run.stopped) void this.requestPull(run, PULL_MIN_GAP_MS);
      },
      online: () => {
        if (run.stopped) return;
        this.flags.offline = false;
        this.refreshStatus();
        void this.requestPull(run);
        if (this.hasPendingToPush()) void this.requestPush(run);
      },
      offline: () => {
        if (run.stopped) return;
        this.flags.offline = true;
        this.refreshStatus();
      },
      beforeUnload: (event) => {
        if (run.stopped || (!this.hasPendingToPush() && !this.flags.pushing)) return;
        // Edits stay in IndexedDB either way, but teammates only see them once pushed: try now and ask the person
        // to wait ("changes not saved yet").
        void this.requestPush(run);
        event.preventDefault();
        event.returnValue = '';
      },
    });
    if (env.isVisible()) {
      this.openDoorbell(run);
      this.startPolling(run);
    }
  }

  private isVisible(): boolean {
    return !this.environment || this.environment.isVisible();
  }

  private startPolling(run: Run): void {
    if (run.pollTimer || run.stopped) return;
    run.pollTimer = setInterval(() => {
      if (run.stopped) return;
      if (!this.isVisible()) return;
      if (this.flags.offline && this.environment && !this.environment.isOnline()) return;
      // While live, rings bring teammates' changes and this pull is only the safety net, about once a minute. Every
      // tick again while the last pull failed (the ring that asked for it is spent) or rings go missing.
      const f = this.flags;
      if (!f.live || f.ringsMissed || f.error !== null) void this.requestPull(run);
      else if (Date.now() - run.lastPullAt >= this.pollIntervalLiveMs) void this.safetyNetPull(run);
      // Retries what an earlier failure (offline, server error, conflict loop) left queued.
      if (this.hasPendingToPush()) void this.requestPush(run);
    }, this.pollIntervalMs);
  }

  private stopPolling(run: Run): void {
    if (run.pollTimer) clearInterval(run.pollTimer);
    run.pollTimer = null;
  }

  // MARK: - Doorbell (the server's "something changed" signal)

  private openDoorbell(run: Run): void {
    const factory = this.doorbell;
    if (!factory || run.doorbell || run.stopped) return;
    const handle = { stop: () => {} };
    run.doorbell = handle;
    try {
      handle.stop = factory(
        run.runtime.companyId,
        (kind, seq) => {
          if (run.doorbell === handle) this.ring(run, kind, seq);
        },
        (live) => {
          if (run.doorbell === handle) this.setLive(run, live);
        },
      );
    } catch (error) {
      console.error('Live updates failed to start', error);
      if (run.doorbell === handle) run.doorbell = null;
    }
  }

  private stopDoorbell(run: Run): void {
    const handle = run.doorbell;
    run.doorbell = null;
    if (!handle) return;
    try {
      handle.stop();
    } catch (error) {
      console.error('Live updates failed to stop', error);
    }
  }

  /** Hidden tab: keep the channel for a minute (a quick tab switch), then let the connection go. */
  private closeDoorbellSoon(run: Run): void {
    if (!run.doorbell || run.doorbellCloseTimer) return;
    run.doorbellCloseTimer = setTimeout(() => {
      run.doorbellCloseTimer = null;
      if (run.stopped || this.isVisible()) return;
      this.stopDoorbell(run);
      this.setLive(run, false);
    }, DOORBELL_HIDDEN_CLOSE_MS);
  }

  private cancelDoorbellClose(run: Run): void {
    if (run.doorbellCloseTimer) clearTimeout(run.doorbellCloseTimer);
    run.doorbellCloseTimer = null;
  }

  private setLive(run: Run, live: boolean): void {
    if (run.stopped || this.flags.live === live) return;
    this.flags.live = live;
    this.refreshStatus();
  }

  /**
   * The once-a-minute pull while live. A subscribed channel only says the join worked, not that the server still
   * rings (the trigger dropped, realtime.send failing, broadcasts lost while the socket stays up). If this pull brings
   * in changes that no ring announced, the page stops trusting the channel: it polls every 15 s and stops saying
   * "Live" until a `changed` ring with a seq arrives. A ring still in flight when the pull ran clears it again.
   */
  private async safetyNetPull(run: Run): Promise<void> {
    const cursor = this.replica.meta.cursor;
    const heard = run.ringsHeard;
    await this.requestPull(run);
    if (run.stopped || !this.flags.live || this.flags.ringsMissed || this.flags.error !== null) return;
    const unannounced = this.replica.meta.cursor > cursor && run.ringsHeard === heard && run.lastRingSeq <= cursor;
    if (!unannounced) return;
    this.flags.ringsMissed = true;
    this.refreshStatus();
  }

  /** A `changed` ring with a seq: the server is ringing. */
  private ringHeard(run: Run, seq: number): void {
    run.ringsHeard += 1;
    run.lastRingSeq = Math.max(run.lastRingSeq, seq);
    if (!this.flags.ringsMissed) return;
    this.flags.ringsMissed = false;
    this.refreshStatus();
  }

  /**
   * A ring: pull soon, unless its seq is already behind the cursor (usually the echo of this tab's own push). Rings
   * within RING_DEBOUNCE_MS of the first share one pull; while the tab is hidden they wait for it to be visible.
   */
  private ring(run: Run, kind: RingKind, seq: number | null): void {
    if (run.stopped || !run.ready) return;
    if (kind === 'changed' && seq !== null) {
      this.ringHeard(run, seq);
      if (seq <= this.replica.meta.cursor) return;
    }
    const pending = run.ring;
    run.ring = {
      seq: seq === null ? pending.seq : Math.max(pending.seq, seq),
      always: pending.always || seq === null,
      team: pending.team || kind === 'team',
    };
    if (run.ringTimer || !this.isVisible()) return;
    run.ringTimer = setTimeout(() => {
      run.ringTimer = null;
      this.answerRing(run);
    }, RING_DEBOUNCE_MS);
  }

  /**
   * Answers the pending rings (visible tab only): team screens refetch on a team ring, and a pull is requested without
   * the minimum gap (it queues behind a pull already running; one waiting pull is shared). True if it asked for a pull.
   */
  private answerRing(run: Run): boolean {
    if (run.stopped || !this.isVisible()) return false;
    if (run.ringTimer) clearTimeout(run.ringTimer);
    run.ringTimer = null;
    const { seq, always, team } = run.ring;
    run.ring = NO_RING;
    if (team) this.emitTeamChanged();
    // A pull since the ring may already have fetched it.
    if (!always && seq <= this.replica.meta.cursor) return false;
    void this.requestPull(run);
    return true;
  }

  // MARK: - Pull

  /** Pulls now (coalesced with a pull already waiting). Errors are reported through the status, never thrown. */
  pullNow(): Promise<void> {
    const run = this.run;
    if (!run || run.stopped) return Promise.resolve();
    return run.started.then(() => this.requestPull(run));
  }

  private requestPull(run: Run, minGapMs = 0): Promise<void> {
    if (run.stopped || !run.ready) return Promise.resolve();
    if (run.pullQueued) return run.pullQueued;
    if (minGapMs > 0 && !this.flags.pulling && Date.now() - run.lastPullAt < minGapMs) return Promise.resolve();
    const queued = run.gate.run(async () => {
      run.pullQueued = null;
      if (run.stopped) return;
      await this.doPull(run);
    });
    run.pullQueued = queued;
    return queued;
  }

  private async doPull(run: Run): Promise<void> {
    this.flags.pulling = true;
    this.refreshStatus();
    try {
      const outcome = await pullOnce(run.runtime, {
        onFullStart: () => {
          if (run.stopped) return;
          this.flags.fullResync = true;
          this.flags.progress = { applied: 0 };
          this.refreshStatus();
        },
        onPage: (applied) => {
          if (run.stopped) return;
          if (this.flags.fullResync) this.flags.progress = { applied };
          this.refreshStatus();
        },
      });
      if (outcome.stopped || run.stopped) return;
      this.flags.error = null;
      this.flags.offline = false;
    } catch (error) {
      this.handleError(run, error);
    } finally {
      run.lastPullAt = Date.now();
      if (!run.stopped) {
        this.flags.pulling = false;
        this.flags.fullResync = false;
        this.flags.progress = null;
      }
      this.refreshStatus();
    }
  }

  // MARK: - Edits and push

  /**
   * Applies patches to the outbox (optimistic: screens show them at once) and schedules a push. Throws when a patch
   * is invalid (nothing is applied then) or while the replica is not loaded (SyncNotReadyError).
   */
  save(patches: readonly Patch[]): SaveResult {
    const run = this.run;
    if (!run || run.stopped || !run.ready) throw new SyncNotReadyError();
    const result = applyPatches(this.replica, patches, this.now);
    if (result.changed > 0) {
      void this.replica.persist();
      this.refreshStatus();
      this.pushSoon(run, this.pushDebounceMs);
    }
    return result;
  }

  /** Drops a local edit (the "Discard" of a failed save). The row shows the server copy again. */
  discard(collection: Collection, id: string): boolean {
    const removed = discardEntry(this.replica, collection, id);
    if (removed) {
      void this.replica.persist();
      this.refreshStatus();
    }
    return removed;
  }

  /** Re-queues failed edits ("Try again"). Returns how many. */
  retryFailed(): number {
    const run = this.run;
    const n = retryFailed(this.replica);
    if (n > 0) {
      void this.replica.persist();
      this.refreshStatus();
      if (run && run.ready && !run.stopped) this.pushSoon(run, 0);
    }
    return n;
  }

  /**
   * Pushes pending edits now and waits at most `timeoutMs` (sign-out, §4.6). `unsaved` counts the outbox entries
   * still not on the server afterwards (failed ones included).
   */
  async flush(timeoutMs = 5000): Promise<FlushResult> {
    const run = this.run;
    const deadline = delay(timeoutMs);
    try {
      if (run && !run.stopped) {
        await Promise.race([run.started, deadline.promise]);
        if (run.ready && !run.stopped && this.hasPendingToPush()) {
          if (run.pushTimer) clearTimeout(run.pushTimer);
          run.pushTimer = null;
          await Promise.race([this.requestPush(run), deadline.promise]);
        }
      }
    } finally {
      deadline.cancel();
    }
    const persisted = delay(1000);
    await Promise.race([this.replica.persist(), persisted.promise]);
    persisted.cancel();
    return { unsaved: this.replica.outboxSize };
  }

  private pushSoon(run: Run, delayMs: number): void {
    if (run.stopped) return;
    if (run.pushTimer) clearTimeout(run.pushTimer);
    run.pushTimer = setTimeout(() => {
      run.pushTimer = null;
      void this.requestPush(run);
    }, delayMs);
  }

  private hasPendingToPush(): boolean {
    return this.replica.entries().some((e) => e.state === 'pending');
  }

  private requestPush(run: Run): Promise<void> {
    if (run.stopped || !run.ready) return Promise.resolve();
    if (run.pushQueued) return run.pushQueued;
    const queued = run.gate.run(async () => {
      run.pushQueued = null;
      if (run.stopped) return;
      await this.doPush(run);
    });
    run.pushQueued = queued;
    return queued;
  }

  private async doPush(run: Run): Promise<void> {
    if (!this.hasPendingToPush()) return;
    const editSeqBefore = this.replica.meta.editSeq;
    this.flags.pushing = true;
    this.refreshStatus();
    try {
      const outcome = await pushOnce(run.runtime);
      if (outcome.stopped || run.stopped) return;
      this.flags.error = null;
      this.flags.offline = false;
      for (const notice of outcome.notices) this.emitNotice(notice);
      if (outcome.saved > 0) {
        // Fetch what the push re-emitted and teammates' changes, and wake teammates' phones.
        void this.requestPull(run);
        this.notifyDevices(run);
      }
    } catch (error) {
      this.handleError(run, error);
    } finally {
      if (!run.stopped) this.flags.pushing = false;
      this.refreshStatus();
    }
    if (run.stopped) return;
    // An edit made while this push was running that did not get its turn goes up after the usual debounce.
    if (this.flags.error === null && this.replica.entries().some((e) => e.state === 'pending' && e.rev > editSeqBefore)) {
      this.pushSoon(run, this.pushDebounceMs);
    }
  }

  private notifyDevices(run: Run): void {
    const send = () => {
      run.lastNotifyAt = Date.now();
      void this.api.notifyDevices(run.runtime.companyId).catch(() => undefined);
    };
    const wait = run.lastNotifyAt + NOTIFY_THROTTLE_MS - Date.now();
    if (wait <= 0) {
      send();
      return;
    }
    if (run.notifyTimer) return;
    run.notifyTimer = setTimeout(() => {
      run.notifyTimer = null;
      if (!run.stopped) send();
    }, wait);
  }

  // MARK: - Errors and removal

  private handleError(run: Run, error: unknown): void {
    if (run.stopped) return;
    if (isSyncApiError(error) && error.isRemoval) {
      void this.handleRemoval(run, error.kind as RemovalKind);
      return;
    }
    const kind: SyncErrorKind = isSyncApiError(error) ? error.kind : 'unknown';
    if (!isSyncApiError(error)) console.error('Sync failed', error);
    this.flags.error = { kind, message: describeSyncError(error) };
    if (kind === 'network') this.flags.offline = true;
  }

  /** not_member / suspended (§3.1 step 4): stop, delete this replica, tell the auth layer. */
  private async handleRemoval(run: Run, kind: RemovalKind): Promise<void> {
    if (run.stopped) return;
    this.removal = kind;
    this.halt(run);
    this.flags.error = { kind, message: describeSyncError(new SyncApiError(kind, kind, 403, '42501')) };
    this.refreshStatus();
    await this.chain(() => this.replica.destroy());
    for (const listener of [...this.removedListeners]) {
      try {
        listener(kind);
      } catch (error) {
        console.error('removal listener failed', error);
      }
    }
  }

  // MARK: - Events

  /** Toasts: deleted elsewhere, permission refusals, last admin, rejected edits. */
  onNotice(listener: (notice: SyncNotice) => void): () => void {
    this.noticeListeners.add(listener);
    return () => {
      this.noticeListeners.delete(listener);
    };
  }

  /** The server says this account is no longer an (active) member of the company. */
  onRemoved(listener: (kind: RemovalKind) => void): () => void {
    this.removedListeners.add(listener);
    return () => {
      this.removedListeners.delete(listener);
    };
  }

  /**
   * The server rang for a team change (members, roles, visibility epoch, company name): SyncProvider broadcasts
   * gt:refresh so screens built on server queries (`team_roster`, `team_metrics`) refetch.
   */
  onTeamChanged(listener: () => void): () => void {
    this.teamListeners.add(listener);
    return () => {
      this.teamListeners.delete(listener);
    };
  }

  private emitTeamChanged(): void {
    for (const listener of [...this.teamListeners]) {
      try {
        listener();
      } catch (error) {
        console.error('team listener failed', error);
      }
    }
  }

  private emitNotice(notice: SyncNotice): void {
    for (const listener of [...this.noticeListeners]) {
      try {
        listener(notice);
      } catch (error) {
        console.error('notice listener failed', error);
      }
    }
  }

  // MARK: - Status (useSyncStatus reads it through useSyncExternalStore)

  getStatus = (): SyncStatus => this.status;

  subscribeStatus = (listener: () => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  };

  private refreshStatus(): void {
    const run = this.run;
    const meta = this.replica.meta;
    const f = this.flags;
    let phase: SyncStatus['phase'];
    if (this.removal) phase = 'error';
    else if (!run || run.stopped || !run.ready) phase = 'starting';
    else if (f.pulling) phase = 'pulling';
    else if (f.pushing) phase = 'pushing';
    else if (f.offline || f.error?.kind === 'network') phase = 'offline';
    else if (f.error) phase = 'error';
    else phase = 'idle';

    const lastIso = meta.lastSyncedAt;
    const previous = this.status;
    const lastSyncedAt =
      lastIso === null
        ? null
        : previous.lastSyncedAt && previous.lastSyncedAt.toISOString() === lastIso
          ? previous.lastSyncedAt
          : new Date(lastIso);
    const error = f.error?.message ?? (phase === 'offline' ? OFFLINE_MESSAGE : null);
    const progress = !f.progress
      ? null
      : previous.progress?.applied === f.progress.applied
        ? previous.progress
        : { applied: f.progress.applied };
    const next: SyncStatus = {
      phase,
      lastSyncedAt,
      pending: this.replica.outboxSize,
      failed: this.replica.failedCount,
      fullResync: f.fullResync,
      progress,
      error,
      initialFetchDone: meta.initialFetchDone,
      live: f.live && !f.ringsMissed,
    };
    if (
      next.phase === previous.phase &&
      next.lastSyncedAt === previous.lastSyncedAt &&
      next.pending === previous.pending &&
      next.failed === previous.failed &&
      next.fullResync === previous.fullResync &&
      (next.progress?.applied ?? null) === (previous.progress?.applied ?? null) &&
      next.error === previous.error &&
      next.initialFetchDone === previous.initialFetchDone &&
      next.live === previous.live
    ) {
      return;
    }
    this.status = next;
    for (const listener of [...this.statusListeners]) {
      try {
        listener();
      } catch (error) {
        console.error('status listener failed', error);
      }
    }
  }
}
